import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, Ban, RefreshCw } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber, formatDate } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { PromotionDto, PromotionStatus, Tone } from '@kardume/types';
import { PROMOTION_TYPE_LABELS, PROMOTION_STATUS_LABELS } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  Avatar,
  SearchInput,
  Select,
  Modal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import PromotionBenefitSummary from '../components/PromotionBenefitSummary';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Ad = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .info {
    min-width: 0;
  }
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .merchant {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textSecondary};
  }
  .id {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
  }
`;

const Period = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Metrics = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textSecondary};

  span {
    display: block;
    color: ${(p) => p.theme.textMuted};
    font-size: 0.75rem;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDING_REVIEW', label: 'Pendentes de revisão' },
  { value: 'ACTIVE', label: 'Aprovados' },
  { value: 'REJECTED', label: 'Rejeitados' },
];

/** Map a promotion status to a Badge tone. */
function statusTone(status: PromotionStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_REVIEW' || status === 'DRAFT' || status === 'PAUSED') return 'warning';
  if (status === 'REJECTED' || status === 'EXPIRED') return 'danger';
  return 'secondary';
}

const ModerationPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listPromotions({ size: 100 }), []);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_REVIEW');
  const [rejecting, setRejecting] = useState<PromotionDto | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const promotions = useMemo(() => data?.content ?? [], [data]);

  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? promotions.length,
      pending: promotions.filter((p) => p.status === 'PENDING_REVIEW').length,
      active: promotions.filter((p) => p.status === 'ACTIVE').length,
      rejected: promotions.filter((p) => p.status === 'REJECTED').length,
    }),
    [data, promotions]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return promotions.filter((p) => {
      const matchesStatus = !statusFilter || p.status === statusFilter;
      const matchesTerm =
        !term || [p.title, p.merchantName ?? '', p.description].some((v) => v.toLowerCase().includes(term));
      return matchesStatus && matchesTerm;
    });
  }, [promotions, search, statusFilter]);

  const approve = async (promotion: PromotionDto): Promise<void> => {
    setSubmittingId(promotion.id);
    try {
      await backofficeApi.moderatePromotion(promotion.id, { approved: true });
      showNotification('Anúncio aprovado com sucesso.', 'success');
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível aprovar o anúncio.'), 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  const openReject = (promotion: PromotionDto): void => {
    setRejecting(promotion);
    setRejectionReason('');
  };

  const confirmReject = async (): Promise<void> => {
    if (!rejecting) return;
    setSubmittingId(rejecting.id);
    try {
      await backofficeApi.moderatePromotion(rejecting.id, {
        approved: false,
        rejectionReason: rejectionReason.trim() || undefined,
      });
      showNotification('Anúncio rejeitado.', 'success');
      setRejecting(null);
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível rejeitar o anúncio.'), 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Moderação de anúncios"
        subtitle="Revise e aprove os anúncios enviados pelas empresas parceiras. A coluna “o que o cliente ganha” traz o benefício e as condições que o motor vai aplicar - é isso que a aprovação cobre."
      />

      <StatsGrid>
        <StatCard label="Total de anúncios" value={formatNumber(stats.total)} />
        <StatCard label="Pendentes de revisão" value={formatNumber(stats.pending)} />
        <StatCard label="Aprovados" value={formatNumber(stats.active)} />
        <StatCard label="Rejeitados" value={formatNumber(stats.rejected)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por título ou empresa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhum anúncio para moderar"
        emptyDescription="Não há anúncios nesta situação no momento."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Anúncio</th>
                <th>O que o cliente ganha</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Vigência</th>
                <th>Desempenho</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((promotion) => (
                <tr key={promotion.id}>
                  <td>
                    <Ad>
                      <Avatar name={promotion.title} src={promotion.imageUrl} />
                      <div className="info">
                        <strong>{promotion.title}</strong>
                        <div className="merchant">{promotion.merchantName ?? 'Empresa'}</div>
                        <div className="id">ID: {promotion.id.slice(0, 8)}...</div>
                      </div>
                    </Ad>
                  </td>
                  <td>
                    <PromotionBenefitSummary promotion={promotion} />
                  </td>
                  <td>
                    <Badge tone="secondary">{PROMOTION_TYPE_LABELS[promotion.type]}</Badge>
                  </td>
                  <td>
                    <Badge tone={statusTone(promotion.status)}>
                      {PROMOTION_STATUS_LABELS[promotion.status]}
                    </Badge>
                  </td>
                  <td>
                    <Period>
                      {formatDate(promotion.startsAt)}
                      {promotion.endsAt ? ` - ${formatDate(promotion.endsAt)}` : ''}
                    </Period>
                  </td>
                  <td>
                    <Metrics>
                      {formatNumber(promotion.views)} visualizações
                      <span>{formatNumber(promotion.redemptions)} resgates</span>
                    </Metrics>
                  </td>
                  <td>
                    <RowActions>
                      <Button
                        variant="primary"
                        isLoading={submittingId === promotion.id}
                        disabled={promotion.status === 'ACTIVE'}
                        onClick={() => approve(promotion)}
                      >
                        <CheckCircle2 size={16} /> Aprovar
                      </Button>
                      <Button
                        variant="danger"
                        disabled={submittingId === promotion.id || promotion.status === 'REJECTED'}
                        onClick={() => openReject(promotion)}
                      >
                        <Ban size={16} /> Rejeitar
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Rejeitar anúncio"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting(null)} disabled={submittingId !== null}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmReject} isLoading={submittingId !== null}>
              Confirmar rejeição
            </Button>
          </>
        }
      >
        <FieldLabel htmlFor="rejection-reason">Motivo da rejeição</FieldLabel>
        <Textarea
          id="rejection-reason"
          value={rejectionReason}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectionReason(e.target.value)}
          placeholder="Descreva o motivo para que a empresa possa ajustar o anúncio."
        />
      </Modal>
    </>
  );
};

export default ModerationPage;
